import React, { useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Check, X, Play, Clock, Pause, Mic, Repeat, Video } from 'lucide-react';
import { Marker } from '../../types/vidmastro';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface MarkerCardProps {
    marker: Marker;
    onApprove: (id: string) => void;
    onReject: (id: string) => void;
    onPreview: (id: string) => void;
    isActive?: boolean;
    className?: string;
}

export function MarkerCard({
    marker,
    onApprove,
    onReject,
    onPreview,
    isActive,
    className
}: MarkerCardProps) {
    const [swipeOffset, setSwipeOffset] = useState(0);

    const handlers = useSwipeable({
        onSwiping: (eventData) => {
            setSwipeOffset(eventData.deltaX);
        },
        onSwipedLeft: () => {
            if (swipeOffset < -100) onReject(marker.id);
            setSwipeOffset(0);
        },
        onSwipedRight: () => {
            if (swipeOffset > 100) onApprove(marker.id);
            setSwipeOffset(0);
        },
        onSwiped: () => {
            setSwipeOffset(0);
        },
        trackMouse: true
    });

    const getIcon = () => {
        switch (marker.type) {
            case 'voice_command': return <Mic className="w-5 h-5 text-red-500" />;
            case 'pause': return <Clock className="w-5 h-5 text-yellow-500" />;
            case 'filler': return <Pause className="w-5 h-5 text-blue-500" />; // Or specific icon
            case 'retake': return <Repeat className="w-5 h-5 text-purple-500" />;
            default: return <Video className="w-5 h-5 text-gray-500" />;
        }
    };

    const getActionText = () => {
        if (marker.type === 'voice_command') return 'Cut Command';
        if (marker.type === 'pause') return `Long Pause (${marker.metadata?.duration?.toFixed(1)}s)`;
        if (marker.type === 'filler') return `Filler "${marker.metadata?.word}"`;
        if (marker.type === 'retake') return 'Retake Detected';
        return 'Suggestion';
    };

    const opacity = Math.max(0.5, 1 - Math.abs(swipeOffset) / 300);
    const transform = `translateX(${swipeOffset}px) rotate(${swipeOffset * 0.05}deg)`;

    // Background color based on swipe
    const bgColor = swipeOffset > 50 ? 'bg-green-100 dark:bg-green-900/30' :
        swipeOffset < -50 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-card';

    return (
        <div
            className={cn(
                "relative w-full max-w-sm mx-auto overflow-hidden rounded-xl border transition-all duration-200",
                isActive ? "border-primary ring-2 ring-primary/20 shadow-lg scale-105 z-10" : "border-border shadow-sm",
                className
            )}
            style={{ touchAction: 'pan-y' }} // Allow vertical scrolling, handle horizontal swipe manually logic inside useSwipeable mostly handles it
        >
            {/* Background Action Indicators */}
            <div className="absolute inset-0 flex justify-between items-center px-6 z-0">
                <div className={cn("text-green-500 font-bold text-xl flex items-center transition-opacity", swipeOffset > 50 ? 'opacity-100' : 'opacity-0')}>
                    <Check className="w-8 h-8 mr-2" /> KEEP
                </div>
                <div className={cn("text-red-500 font-bold text-xl flex items-center transition-opacity", swipeOffset < -50 ? 'opacity-100' : 'opacity-0')}>
                    CUT <X className="w-8 h-8 ml-2" />
                </div>
            </div>

            <div
                {...handlers}
                className={cn("relative z-10 p-4 flex flex-col gap-3 transition-transform cursor-grab active:cursor-grabbing", bgColor)}
                style={{ transform, opacity }}
            >
                <div className="flex justify-between items-start">
                    <div className="flex gap-3 items-center">
                        <div className="p-2 bg-background rounded-full shadow-sm">
                            {getIcon()}
                        </div>
                        <div>
                            <h3 className="font-semibold text-foreground">{getActionText()}</h3>
                            <p className="text-xs text-muted-foreground">
                                {(marker.timestampStart).toFixed(1)}s - {(marker.timestampEnd).toFixed(1)}s
                            </p>
                        </div>
                    </div>
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); onPreview(marker.id); }}
                    >
                        <Play className="w-4 h-4" />
                    </Button>
                </div>

                {/* Manual Buttons for non-swipers */}
                <div className="flex gap-2 mt-1">
                    <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                        onClick={(e) => { e.stopPropagation(); onReject(marker.id); }}
                    >
                        <X className="w-4 h-4 mr-1" />
                        Reject (Cut)
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950"
                        onClick={(e) => { e.stopPropagation(); onApprove(marker.id); }}
                    >
                        <Check className="w-4 h-4 mr-1" />
                        Approve (Keep)
                    </Button>
                </div>
            </div>
        </div>
    );
}
